import { validateResolution } from "./resolutionValidation";

/**
 * Centralised validation for the wallpaper add/edit forms so both screens
 * stay in sync. Returns a map of `{ fieldName: errorMessage }` containing
 * only the fields that currently fail validation.
 *
 * Theme and resolution are OPTIONAL: an empty theme creates a Global
 * Wallpaper, and resolution may be left blank. Both are still validated
 * for correct format when a value is provided.
 *
 * @param {object} values
 * @param {string} values.theme_id
 * @param {string} values.wallpaper_name
 * @param {string} values.resolution
 * @param {string} values.resolution_ipad
 * @param {File|string|null} [values.thumbnail] - file (add) or existing url (edit)
 * @param {File|string|null} [values.wallpaper] - file (add) or existing url (edit)
 * @param {{ requireFiles?: boolean }} [options] - require image files (true on add).
 * @returns {Record<string, string>}
 */
export const buildWallpaperErrors = (values, options = {}) => {
  const { requireFiles = false } = options;
  const errors = {};

  // Theme is optional — empty = Global Wallpaper.
  if (!values.wallpaper_name || !values.wallpaper_name.trim()) {
    errors.wallpaper_name = "Wallpaper name is required";
  }

  // Resolution is optional, but must be a valid format when provided.
  const resolutionError = validateResolution(values.resolution, {
    required: false,
    label: "Resolution",
  });
  if (resolutionError) errors.resolution = resolutionError;

  // iPad resolution is optional, but must be a valid format when provided.
  const ipadResolutionError = validateResolution(values.resolution_ipad, {
    required: false,
    label: "iPad resolution",
  });
  if (ipadResolutionError) errors.resolution_ipad = ipadResolutionError;

  if (requireFiles) {
    if (!values.thumbnail) errors.thumbnail = "Thumbnail image is required";
    if (!values.wallpaper) errors.wallpaper = "Wallpaper image is required";
  }

  return errors;
};

/** True when the supplied error map has no entries. */
export const isWallpaperFormValid = (errors) =>
  Object.keys(errors).length === 0;
